import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { router } from './router'
import './index.css'
import { loadConfig } from './config'
import { getKeycloak } from './auth/keycloak'
import { AuthProvider } from './auth/AuthContext'
import { wireProjectSubscriptions } from './projects/projectSubscriptions'
import { hydrateProjectContext, hydrateProjects } from './projects/projectHydration'
import { authTokenRef } from './auth/tokenRef'
import { enforceSessionBoundary } from './auth/sessionGuard'
import { useOrgStore } from './stores/useOrgStore'
import { useProjectStore } from './stores/useProjectStore'
import { useCollaborationStore } from './stores/useCollaborationStore'
import { useAnalyticsProductsStore } from "./stores/useAnalyticsProductsStore";
import { installOrgContextInvalidation } from "./stores/usePlatformOrgContext";
import { useImageryStackStore } from "./stores/useImageryStackStore";
import { useAIOnDemandStore } from "./stores/useAIOnDemandStore";

// For DEBUG
import { useLayerRegistryStore } from './stores/useLayerRegistryStore';
import { useLayerSetsStore } from './stores/useLayerSetsStore';

// Bootstrap:
//   1. Load runtime config from /config.json
//   2. Construct Keycloak with config values
//   3. Run keycloak.init() and existing bootstrap
//   4. Render <App />
loadConfig()
  .then(() => {
    const keycloak = getKeycloak()

    return keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (!authenticated) {
          keycloak.login()
          return
        }

        // Token refresh — keep authTokenRef in sync for fetchWithAuth
        keycloak.onTokenExpired = () => {
          keycloak
            .updateToken(60)
            .then((refreshed) => {
              if (refreshed) authTokenRef.current = keycloak.token
            })
            .catch(() => console.warn('KC refresh failed'))
        }

        keycloak.onAuthRefreshSuccess = () => {
          authTokenRef.current = keycloak.token
        }

        // Make KC available synchronously to the router's beforeLoad guards
        // and to AuthContext/useAuthStore which read via window.__keycloak.
        window.__keycloak = keycloak

        // Clear cross-user state at login. enforceSessionBoundary wipes the
        // previous user's persisted localStorage keys, but the persist-backed
        // stores (useOrgStore / useProjectStore) already hydrated the PREVIOUS
        // user's values at module-import time — which runs before this
        // bootstrap callback. On a different-user login, reset their IN-MEMORY
        // state now, before any fetch fires. Otherwise the stale activeOrgId /
        // activeProjectId leaks into the X-Active-Org / x-aiprime-project
        // headers on the first authenticated request (/orgs/me,
        // /project/context): the backend returns "no membership" for the new
        // user, activeOrgId resolves to null, and the analytics-products ->
        // TopNav mode-strip chain stays empty until a manual refresh (which
        // boots with the already-wiped storage and so sends no stale header).
        const userChanged = enforceSessionBoundary()
        if (userChanged) {
          useOrgStore.getState().clearOrg()
          useProjectStore.getState()._clearUserScoped()
        }

        wireProjectSubscriptions()
        hydrateProjects()
        hydrateProjectContext()

        useOrgStore.subscribe((state, prevState) => {
          if (state.activeOrgId === prevState.activeOrgId) return;
          if (!state.activeOrgId) {
            useImageryStackStore.setState({
              catalog: { series: [] },
              stack: [],
            });
          }
          // Note: catalog load is driven by useSyncOperatingAoiImagery, which fires
          // when activeOperatingAoiId is set by useOperatingAoiInit after org load.
        });

        // Org changes on THIS store — the caller's own org. Impersonation does not
        // touch it; that is usePlatformOrgContext, handled separately below. This
        // comment used to say "(impersonation, switching orgs mid-session)" and it
        // was never true of a useOrgStore subscription.
        useOrgStore.subscribe((state, prevState) => {
          if (state.activeOrgId === prevState.activeOrgId) return;
          if (state.activeOrgId) {
            useAnalyticsProductsStore.getState().load();
          } else {
            useAnalyticsProductsStore.getState().clear();
          }
        });

        // Impersonation changes invalidate by reloading. The rule, the reason a
        // reload beats targeted store invalidation, and the guard that stops it
        // looping all live with the store it watches — see
        // stores/usePlatformOrgContext.js. Nothing subscribed to that store
        // before this; the subscription above watches useOrgStore, which
        // impersonation never writes.
        installOrgContextInvalidation();

        // Initial load. After loadActiveOrg resolves, kick off the analytics
        // products load explicitly — the subscription above won't fire if the
        // org was already populated from persisted state.
        useOrgStore.getState().loadActiveOrg().then(() => {
          const orgId = useOrgStore.getState().activeOrgId;
          if (orgId) {
            useAnalyticsProductsStore.getState().load();
          }
        });

        // Load notifications immediately, then poll every 60s.
        // Runs outside React so the interval survives route changes.
        const { loadNotifications } = useCollaborationStore.getState()
        loadNotifications()
        // No teardown: the interval ends with the document. (An unload listener
        // that cleared it was dead, and Chrome no longer allows unload; see
        // noUnloadListener.test.js.)
        setInterval(loadNotifications, 60_000) // safety net — SSE handles real-time

        if (typeof window !== "undefined") {
          window.__stores = {
            layerRegistry: useLayerRegistryStore,
            layerSets: useLayerSetsStore,
            imageryStack: useImageryStackStore,
            aiOnDemand: useAIOnDemandStore,
          };
        }

        ReactDOM.createRoot(document.getElementById('root')).render(
          <React.StrictMode>
            {/*
              AuthProvider stays — components that call useAuth() still work.
              RouterProvider replaces the bare <App /> render.
              No ordering issue: KC is already authenticated before we get here.
            */}
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </React.StrictMode>
        )
      })
  })
  .catch((err) => {
    console.error('Bootstrap failed:', err)
    document.getElementById('root').innerHTML = `
      <div style="padding:2em;font-family:sans-serif;">
        <h1>Application failed to start</h1>
        <p>The application could not load its configuration or authenticate.</p>
        <pre style="background:#f4f4f4;padding:1em;">${err.message}</pre>
      </div>
    `
  })